// OrderAlarm - app-wide drink / dessert / chips alarm.
// Mounted once in App for admin screens, on every page. Listens for
// 'orderAlert' from the server, shows a huge flashing banner, plays a siren
// and reads the items out loud, then clears itself. No acknowledgment needed.
// Reports back to the server whether this screen actually played the sound,
// and sends a heartbeat with its sound state so the owner can be told when
// no screen at the shop would hear an alarm.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import socket from '../config/socket';
import { API_URL } from '../config/api';
import './orderAlarm.css';

const api = axios.create({ baseURL: API_URL, withCredentials: true });
const SCREEN_ID_KEY = 'bb_screen_id';

// Stable per-browser id so the server can tie heartbeats and alarm receipts
// to one screen (and to its IP, which decides whether it is at the shop).
function screenId() {
  try {
    let id = localStorage.getItem(SCREEN_ID_KEY);
    if (!id) {
      id = `scr-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
      localStorage.setItem(SCREEN_ID_KEY, id);
    }
    return id;
  } catch {
    return 'scr-nostorage';
  }
}

const EMOJI = { drinks: '🥤', desserts: '🍰', chips: '🌮', takeout: '🥢' };
const LABEL = { drinks: 'DRINKS', desserts: 'DESSERT', chips: 'CHIPS', takeout: 'TAKE-OUT ITEMS' };
const SOUND_KEY = 'bb_alarm_sound';
const SCREEN_NAME_KEY = 'bb_screen_name';
const VOLUME_KEY = 'bb_alarm_volume'; // 0-100, per computer

function readVolume() {
  try {
    const v = Number(localStorage.getItem(VOLUME_KEY));
    return Number.isFinite(v) && localStorage.getItem(VOLUME_KEY) !== null ? Math.min(100, Math.max(0, v)) : 70;
  } catch { return 70; }
}
export function getVolume() { return readVolume() / 100; }

function readSoundPref() {
  try { return localStorage.getItem(SOUND_KEY) !== 'off'; } catch { return true; }
}
function readScreenName(fallback) {
  try { return localStorage.getItem(SCREEN_NAME_KEY) || fallback; } catch { return fallback; }
}

// Siren: three rising sweeps through Web Audio (no audio file needed).
function playSiren(ctx, volume = getVolume()) {
  const now = ctx.currentTime;
  const peak = Math.max(0.0002, 0.5 * volume);
  for (let i = 0; i < 3; i++) {
    const t0 = now + i * 0.75;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(600, t0);
    osc.frequency.exponentialRampToValueAtTime(1400, t0 + 0.35);
    osc.frequency.exponentialRampToValueAtTime(700, t0 + 0.65);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.7);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.72);
  }
}

// Voice choice. The browser's default voice is the robotic one; most Macs and
// Chrome installs ship far better ones. Prefer a saved pick, then known good
// voices, then any English voice. Exported so the settings page can list them.
export const VOICE_KEY = 'bb_alarm_voice';
const PREFERRED = [/google us english/i, /samantha/i, /^ava/i, /allison/i, /zoe/i, /nicky/i, /karen/i, /moira/i, /daniel/i, /google uk english female/i];

export function listVoices() {
  if (!('speechSynthesis' in window)) return [];
  return window.speechSynthesis.getVoices().filter(v => /^en/i.test(v.lang));
}

export function pickVoice() {
  const voices = listVoices();
  if (voices.length === 0) return null;
  let saved = null;
  try { saved = localStorage.getItem(VOICE_KEY); } catch {}
  if (saved) {
    const v = voices.find(x => x.name === saved);
    if (v) return v;
  }
  for (const re of PREFERRED) {
    const v = voices.find(x => re.test(x.name));
    if (v) return v;
  }
  return voices.find(v => /en-US/i.test(v.lang)) || voices[0];
}

export function speak(text) {
  if (!('speechSynthesis' in window)) return false;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = pickVoice();
    if (voice) { u.voice = voice; u.lang = voice.lang; } else { u.lang = 'en-US'; }
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = getVolume();
    window.speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
}

export default function OrderAlarm({ enabled, name }) {
  const [soundEnabled, setSoundEnabled] = useState(readSoundPref);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [alert, setAlert] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // null until the server has answered a heartbeat; false = not at the shop
  const [atShop, setAtShop] = useState(null);
  const [volume, setVolume] = useState(readVolume);
  const [panelOpen, setPanelOpen] = useState(false);
  const setVolumePersist = (v) => {
    const n = Math.min(100, Math.max(0, Number(v) || 0));
    setVolume(n);
    try { localStorage.setItem(VOLUME_KEY, String(n)); } catch {}
  };
  const testSound = () => {
    unlock();
    if (ctxRef.current && ctxRef.current.state === 'running') playSiren(ctxRef.current);
    setTimeout(() => speak('This is how loud the order alarm is on this screen.'), 2300);
  };
  // The header slot renders after this component; re-render once mounted so the icon lands in it.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const ctxRef = useRef(null);
  const timersRef = useRef([]);      // setTimeout handles
  const intervalsRef = useRef([]);   // setInterval handles
  const titleRef = useRef(document.title);
  const screenName = readScreenName(name || 'Counter screen');

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    intervalsRef.current.forEach(clearInterval);
    timersRef.current = [];
    intervalsRef.current = [];
    document.title = titleRef.current;
  };

  // Browsers only allow sound after a click/keypress. Unlock on the first
  // gesture anywhere on the page and keep the context alive.
  const unlock = useCallback(() => {
    try {
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) ctxRef.current = new AC();
      }
      const ctx = ctxRef.current;
      if (ctx && ctx.state === 'suspended') ctx.resume();
      if ('speechSynthesis' in window) {
        window.speechSynthesis.getVoices(); // triggers async voice loading in Chrome
        // A silent utterance primes speech on browsers that need a gesture.
        const u = new SpeechSynthesisUtterance('');
        u.volume = 0;
        window.speechSynthesis.speak(u);
      }
      setAudioUnlocked(!!ctx && ctx.state !== 'suspended');
      if (ctx) ctx.onstatechange = () => setAudioUnlocked(ctx.state === 'running');
    } catch (e) {
      console.error('Audio unlock failed', e);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const handler = () => unlock();
    window.addEventListener('pointerdown', handler);
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [enabled, unlock]);

  // Heartbeat: tell the server what this screen can do right now.
  useEffect(() => {
    if (!enabled) return undefined;
    // Over HTTP (not the socket) so the server sees this screen's real IP.
    const send = () => api.post('/alerts/screen', {
      screenId: screenId(),
      name: screenName,
      soundEnabled,
      audioUnlocked,
      visible: document.visibilityState === 'visible',
      page: window.location.pathname,
    }).then(r => setAtShop(!!r.data.atShop)).catch(() => {});
    send();
    const iv = setInterval(send, 30000);
    socket.on('connect', send);
    document.addEventListener('visibilitychange', send);
    return () => {
      clearInterval(iv);
      socket.off('connect', send);
      document.removeEventListener('visibilitychange', send);
    };
  }, [enabled, soundEnabled, audioUnlocked, screenName]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try { localStorage.setItem(SOUND_KEY, next ? 'on' : 'off'); } catch {}
    if (next) unlock();
  };

  // Incoming alarms
  useEffect(() => {
    if (!enabled) return undefined;
    const onAlert = (a) => {
      clearTimers();
      setAlert(a);
      const total = Number(a.overlaySeconds) || 40;
      setSecondsLeft(total);

      const canSound = soundEnabled && ctxRef.current && ctxRef.current.state === 'running';
      let played = false;
      if (canSound) {
        try {
          playSiren(ctxRef.current);
          played = true;
        } catch (e) { console.error('Siren failed', e); }
        timersRef.current.push(setTimeout(() => { if (speak(a.speech)) played = true; }, 2300));
        if (a.repeatVoice !== false) {
          timersRef.current.push(setTimeout(() => speak(a.speech), Math.min(total - 8, 18) * 1000));
        }
        timersRef.current.push(setTimeout(() => {
          api.post('/alerts/heard', { alertId: a.alertId, screenId: screenId(), played }).catch(() => {});
        }, 2600));
      } else if (document.visibilityState === 'visible') {
        api.post('/alerts/heard', { alertId: a.alertId, screenId: screenId(), played: false }).catch(() => {});
      }

      // Flash the tab title too, so a background tab still shows it.
      titleRef.current = document.title.startsWith('🚨') ? titleRef.current : document.title;
      let flip = false;
      intervalsRef.current.push(setInterval(() => {
        flip = !flip;
        document.title = flip ? `🚨 ${Object.keys(a.flagged).map(c => LABEL[c]).join(' + ')} ON ORDER` : titleRef.current;
      }, 900));
      intervalsRef.current.push(setInterval(() => setSecondsLeft(s => (s > 0 ? s - 1 : 0)), 1000));
      timersRef.current.push(setTimeout(() => {
        clearTimers();
        setAlert(null);
      }, total * 1000));
    };
    socket.on('orderAlert', onAlert);
    return () => socket.off('orderAlert', onAlert);
  }, [enabled, soundEnabled, screenName]);

  const dismiss = () => {
    clearTimers();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setAlert(null);
  };

  if (!enabled) return null;

  const state = !soundEnabled ? 'off' : audioUnlocked ? 'on' : 'locked';
  const icon = state === 'on' ? '🔊' : '🔇';
  const where = atShop === false ? ' This screen is NOT at the shop, so it does not count as anyone seeing or hearing an alarm.' : '';
  const title = (state === 'locked'
    ? 'Alarm sound is on but the browser needs one click first. Click here.'
    : state === 'on' ? 'Alarm sound ON on this screen. Click to turn off.' : 'Alarm sound OFF on this screen. Click to turn on.') + where;
  const button = (
    <span className="bb-sound-wrap">
      <button
        type="button"
        className={`bb-sound-icon ${state}${atShop === false ? ' offsite' : ''}`}
        onClick={() => { if (state === 'locked') unlock(); setPanelOpen(o => !o); }}
        title={title}
        aria-label={title}
      >
        {icon}
      </button>
      {panelOpen && (
        <div className="bb-sound-panel" onClick={e => e.stopPropagation()}>
          <div className="bb-sound-row">
            <span>Alarm sound</span>
            <button type="button" className={`bb-sound-toggle ${soundEnabled ? 'on' : ''}`} onClick={toggleSound}>{soundEnabled ? 'ON' : 'OFF'}</button>
          </div>
          <label className="bb-sound-row">
            <span>Volume {volume}%</span>
            <input type="range" min="0" max="100" step="5" value={volume} onChange={e => setVolumePersist(e.target.value)} />
          </label>
          <div className="bb-sound-row">
            <button type="button" className="bb-sound-test" onClick={testSound} disabled={!soundEnabled}>▶ Test on this screen</button>
            <button type="button" className="bb-sound-close" onClick={() => setPanelOpen(false)}>Close</button>
          </div>
          {state === 'locked' && <div className="bb-sound-note">Click anywhere on the page once so the browser allows sound.</div>}
          {atShop === false && <div className="bb-sound-note">This screen is not at the shop; it does not count as hearing an alarm.</div>}
        </div>
      )}
    </span>
  );
  const slot = mounted ? document.getElementById('bb-sound-slot') : null;

  return (
    <>
      {slot ? createPortal(button, slot) : button}

      {alert && (
        <div className="bb-alarm-overlay" onClick={dismiss} role="alert">
          <div className="bb-alarm-title">🚨 DON'T FORGET 🚨</div>
          <div className="bb-alarm-cats">
            {Object.keys(alert.flagged).map(c => `${EMOJI[c]} ${LABEL[c]}`).join('   ·   ')}
          </div>
          <ul className="bb-alarm-items">
            {Object.keys(alert.flagged).flatMap(c => alert.flagged[c].map((f, i) => (
              <li key={`${c}-${i}`}>{EMOJI[c]} {f.quantity}× {f.name}</li>
            )))}
          </ul>
          <div className="bb-alarm-meta">
            {alert.isTest ? 'TEST ALARM · ' : ''}{atShop === false ? 'OFF-SITE SCREEN (does not count) · ' : ''}
            {alert.order.source} {alert.order.orderType || ''}
            {alert.order.customer ? ` · ${alert.order.customer}` : ''}
            {alert.order.orderTime ? ` · ${alert.order.orderTime}` : ''}
            {alert.order.id ? ` · #${alert.order.id}` : ''}
            {alert.order.total != null ? ` · $${Number(alert.order.total).toFixed(2)}` : ''}
          </div>
          <div className="bb-alarm-hint">clears itself in {secondsLeft}s · click anywhere on the alarm to clear it now</div>
        </div>
      )}
    </>
  );
}
